﻿import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { LoginStateService } from './login-state.service';

export interface SavedCompound {
  compound_id: string;
  label: string;
  saved_on: string;
}

@Injectable()
export class FavoritesService {
  private email: string = null;
  private ids: Set<string> = new Set();

  favorites$: BehaviorSubject<SavedCompound[]> = new BehaviorSubject<SavedCompound[]>([]);

  constructor(private http: HttpClient, private loginState: LoginStateService) {
    loginState.isUserLoggedIn.subscribe(state => {
      if (state.loggedIn && state.email) {
        this.email = state.email;
        this.load();
      } else {
        this.email = null;
        this.ids.clear();
        this.favorites$.next([]);
      }
    });
  }

  private get authHeaders(): HttpHeaders {
    return new HttpHeaders().set('Authorization', localStorage.getItem('auth_token') || '');
  }

  isFavorite(compoundId: string): boolean {
    return this.ids.has(compoundId);
  }

  toggle(compoundId: string, label: string): void {
    if (!this.email) { return; }
    if (this.isFavorite(compoundId)) {
      this.remove(compoundId);
    } else {
      this.add(compoundId, label);
    }
  }

  private load(): void {
    this.http.get<{ success: boolean; data: SavedCompound[] }>(
      `https://crestdb.org/api/favorites?email=${encodeURIComponent(this.email)}`, { headers: this.authHeaders }
    ).subscribe({
      next: (res) => {
        if (res.success) {
          this.ids = new Set(res.data.map(f => f.compound_id));
          this.favorites$.next(res.data);
        }
      },
      error: () => {}
    });
  }

  private add(compoundId: string, label: string): void {
    this.ids.add(compoundId);
    const prev = this.favorites$.value;
    this.favorites$.next([{ compound_id: compoundId, label: label, saved_on: new Date().toISOString() }, ...prev]);

    this.http.post('https://crestdb.org/api/favorites', { email: this.email, compound_id: compoundId, label: label }, { headers: this.authHeaders })
      .subscribe({
        error: () => {
          this.ids.delete(compoundId);
          this.favorites$.next(this.favorites$.value.filter(f => f.compound_id !== compoundId));
        }
      });
  }

  private remove(compoundId: string): void {
    this.ids.delete(compoundId);
    const prev = this.favorites$.value;
    this.favorites$.next(prev.filter(f => f.compound_id !== compoundId));

    this.http.delete(`https://crestdb.org/api/favorites/${encodeURIComponent(compoundId)}?email=${encodeURIComponent(this.email)}`, { headers: this.authHeaders })
      .subscribe({
        error: () => {
          this.ids.add(compoundId);
          this.favorites$.next(prev);
        }
      });
  }
}
